import Vue from 'vue'
import {
  find,
  propEq,
  path,
  prop,
  pipe,
  filter,
  ifElse,
  map,
  any,
  append,
  reduce,
  converge,
  concat,
  groupBy,
  pathOr,
  propOr,
  either,
  equals,
  max,
} from 'ramda'
import BookingDetailsApiModel, {
  PaymentApiModel,
  BookingVoucherApiModel,
  BookingDiscountApiModel,
  BookingMailApiModel,
  PaymentStatus,
} from './BookingDetailsApiModel.js'
import { parseAxiosException } from '@/helpers/exceptionParser.js'
import {
  getSubtotalIncludingTax,
  getSubtotalExcludingTax,
  getTotalExcludingTax,
  getTotalIncludingTax,
  getTaxesByName,
  totalDiscountsAndVouchersBeforeTax,
  totalDiscountsAndVouchersAfterTax,
} from './bookingCostsUtil.js'
import { sumProp } from '@/vue/util/briqRamdaExt.js'

const buildModule = apiClient => ({
  namespaced: true,

  state: {
    bookingDetails: null,
    apiResponse: null,
    isFetching: false,
    fetchError: null,
    isSaving: false,
    saveError: null,
  },

  mutations: {
    setBookingDetails: (state, payload) => Vue.set(state, 'bookingDetails', payload),
    setIsFetching: (state, payload) => (state.isFetching = payload),
    setFetchError: (state, payload) => (state.fetchError = payload),
    setApiResponse: (state, payload) => (state.apiResponse = payload),
    setIsSaving: (state, payload) => (state.isSaving = payload),
    setSaveError: (state, payload) => (state.saveError = payload),
    // booking
    setAttendanceStatus: (state, status) =>
      (state.bookingDetails.Booking.AttendanceStatus = status),
    setOnlineBookingCheckedUser: (state, bookingCheckedUser) =>
      (state.bookingDetails.Booking.OnlineBookingCheckedUser = bookingCheckedUser),
    setBookingStatus: (state, status) => (state.bookingDetails.Booking.CurrentStatusCode = status),
    setBookingCustomers: (state, customers) =>
      Vue.set(state.bookingDetails, 'Customers', customers),
    upsertDiscount: (state, discount) => {
      const newDiscounts = ifElse(
        any(d => d.Key === discount.Key),
        map(d => (d.Key === discount.Key ? discount : d)),
        append(discount),
      )(state.bookingDetails.BookingCosts.Discounts)
      state.bookingDetails.BookingCosts.Discounts = newDiscounts
    },
    addVoucher: (state, voucher) => state.bookingDetails.BookingCosts.Vouchers.push(voucher),
    removeDiscount: (state, discountKey) => {
      state.bookingDetails.BookingCosts.Discounts = state.bookingDetails.BookingCosts.Discounts.filter(
        x => x.Key !== discountKey,
      )
    },
    removeVoucher: (state, voucherKey) => {
      state.bookingDetails.BookingCosts.Vouchers = state.bookingDetails.BookingCosts.Vouchers.filter(
        x => x.Key !== voucherKey,
      )
    },
    setNextBillingMethod: (state, billingMethod) =>
      (state.bookingDetails.Booking.NextBillingMethod = billingMethod),
    // customer
    addCustomer: (state, customer) => state.bookingDetails.Customers.push(customer),
    // visitors
    setVisitors: (state, visitors) => (state.bookingDetails.Visitors = visitors),
    // notes
    setExternalRemark: (state, remark) => (state.bookingDetails.Remarks['externalRemark'] = remark),
    setInternalRemark: (state, remark) => (state.bookingDetails.Remarks['internalRemark'] = remark),
    setReference: (state, reference) => (state.bookingDetails.Booking.Reference = reference),
    // payments
    addPayment: (state, payment) => state.bookingDetails.Payments.push(payment),
    subtractRefundableAmount: (state, { paymentKey, amount }) => {
      const payment = state.bookingDetails.Payments.find(propEq('Key', paymentKey))
      payment.RefundableAmount = payment.RefundableAmount - amount
    },
    // mail
    addMail: (state, mail) => state.bookingDetails.Mails.push(mail),
  },

  actions: {
    async fetchBookingDetails({ commit }, bookingKey) {
      commit('setIsFetching', true)
      commit('setFetchError', null)
      try {
        const response = await apiClient.get(`bookings/${bookingKey}/details`)
        commit('setApiResponse', response)
        const model = new BookingDetailsApiModel(response.data)
        commit('setBookingDetails', model)
      } catch (e) {
        console.error(e)
        commit('setFetchError', {
          rawError: e,
          parsedError: parseAxiosException(e),
        })
      }
      commit('setIsFetching', false)
    },
    async saveFinancialContact({ commit, getters }, customer) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/customers`, customer)
      const handleResponse = () => commit('addCustomer', customer)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async saveVisitors({ commit, getters }, visitors) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/visitors/bulk`, visitors)
      const handleResponse = () => commit('setVisitors', visitors)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async saveReference({ commit, getters }, reference) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/reference`, reference)
      const handleResponse = () => commit('setReference', reference)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async saveRemark({ commit, getters }, remark) {
      const isNew = !remark.Key
      const saveCall = isNew
        ? async () => apiClient.post(`bookings/${getters.bookingKey}/remarks`, remark)
        : async () => apiClient.put(`bookings/${getters.bookingKey}/remarks/${remark.Key}`, remark)
      const handleResponse = r => {
        r.data.Private ? commit('setInternalRemark', r.data) : commit('setExternalRemark', r.data)
      }
      const saved = await saveAction({ saveCall, handleResponse })({ commit })
      const bookingKey = getters.bookingKey
      return { saved, bookingKey }
    },
    async saveBookingStatus({ commit, getters }, status) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/setstatus`, status)
      const handleResponse = () => commit('setBookingStatus', status.NewStatus)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async saveAttendanceStatus({ commit, getters }, { status, forceRedo }) {
      const url = `bookings/${getters.bookingKey}/attendance/${status}`
      const query = forceRedo ? '?forceRedo=true' : ''
      const saveCall = async () => apiClient.post(url + query)
      const handleResponse = () => commit('setAttendanceStatus', status)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async changeMarkAsCheckedStatus({ commit, getters }, { checkedIn }) {
      const url = `bookings/${getters.bookingKey}/mark-as-checked/${checkedIn}`
      const saveCall = async () => apiClient.post(url)
      const handleResponse = () => commit('setOnlineBookingCheckedUser', checkedIn)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },

    async savePayment({ commit, getters }, payment) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/details/addexternalpayment`, payment)
      const handleResponse = ({ data }) => commit('addPayment', new PaymentApiModel(data))
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async refundPayment({ commit, getters }, { paymentKey, postModel }) {
      const saveCall = async () => apiClient.post(`payments/${paymentKey}/refund`, postModel)
      const handleResponse = ({ data }) => {
        commit('addPayment', new PaymentApiModel(data))
        commit('subtractRefundableAmount', { paymentKey, amount: postModel.Amount })
      }
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async sendEmail({ commit, getters }, payload) {
      const saveCall = async () => apiClient.post('bookings/sendbookingmail', payload)
      const handleResponse = ({ data }) => {
        commit('addMail', new BookingMailApiModel(data))
      }
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async checkVoucher({ commit, getters }, voucher) {
      commit('setIsSaving', true) // using this to make sure isPending getter returns true
      const res = await apiClient
        .post(`bookings/${getters.bookingKey}/vouchers/checkcode`, voucher)
        .finally(() => {
          commit('setIsSaving', false)
        })
      return res.data
    },
    async saveVoucher({ commit, getters }, voucher) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/vouchers/redeem`, voucher)
      const handleResponse = ({ data }) => commit('addVoucher', new BookingVoucherApiModel(data))
      return await saveAction({ saveCall, handleResponse })({ commit })
    },

    async saveIntegrationFields({ commit, getters }, payload) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/integration/fields`, payload)
      const handleResponse = ({ data }) => {

      }

      return await saveAction({ saveCall, handleResponse })({ commit })
    },

    async peformIntegrationActionRedirect({ commit, getters }, payload) {
      const saveCall = async () =>
        apiClient.post(
          `bookings/${getters.bookingKey}/integration/${payload.identificationCode}/action/${payload.actionCode}`,
        )
      const handleResponse = ({ data }) => {
        window.open(data.RedirectUrl, '_blank')
      }

      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async saveDiscount({ commit, getters }, payload) {
      const saveCall = async () =>
        apiClient.put(`bookings/${getters.bookingKey}/discounts/upsert`, payload)
      const handleResponse = ({ data }) =>
        commit('upsertDiscount', new BookingDiscountApiModel(data))
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async rollbackVoucher({ commit, getters }, payload) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/vouchers/rollback`, payload)
      const handleResponse = ({ data }) => {
        commit('removeVoucher', payload.Key)
        if (data?.Key) commit('addPayment', data) // A refund payment may have been created
      }
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async removeDiscount({ commit, getters }, payload) {
      const saveCall = async () =>
        apiClient.delete(`bookings/${getters.bookingKey}/discounts/${payload}`)
      const handleResponse = () => commit('removeDiscount', payload)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async changeCustomer({ commit, getters }, customers) {
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/customers/update`, customers)
      const handleResponse = ({ data }) => commit('setBookingCustomers', data)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
    async markAsInvoice({ commit, getters }) {
      const billingMethod = 'InvoiceAfter'
      const saveCall = async () =>
        apiClient.post(`bookings/${getters.bookingKey}/setnextbillingmethod`, billingMethod)
      const handleResponse = () => commit('setNextBillingMethod', billingMethod)
      return await saveAction({ saveCall, handleResponse })({ commit })
    },
  },

  getters: {
    isPending: state => state.isSaving || state.isFetching,
    booking: state => state.bookingDetails?.Booking,
    bookingKey: state => state.bookingDetails?.Booking.Key,
    bookingProducts: state => state.bookingDetails?.BookingProducts,
    bookingCosts: state => state.bookingDetails?.BookingCosts,
    discounts: state => state.bookingDetails?.Discounts,
    vouchers: state => state.bookingDetails?.Vouchers,
    customers: state => state.bookingDetails?.Customers,
    mainBooker: state => find(propEq('Role', 'MainBooker'))(state.bookingDetails?.Customers || []),
    externalRemark: state => state.bookingDetails.Remarks.filter(r => r.Private === false)[0],
    internalRemark: state => state.bookingDetails.Remarks.filter(r => r.Private === true)[0],

    reference: path(['bookingDetails', 'Booking', 'Reference']),
    visitors: state => state.bookingDetails?.Visitors,
    visitorsLength: (state, getters) => getters.visitors?.filter(visitorIsAdded).length || 0,
    payments: state => state.bookingDetails?.Payments,
    amountPaid: (state, getters) =>
      pipe(
        filter(x => x.Status === PaymentStatus.Success || (x.Amount < 0 && x.Status === PaymentStatus.Pending)),
        sumProp('Amount'),
      )(getters.payments || []),
    outstandingAmount: (state, getters) => {
      const diff = getters.totalCostIncludingTax - getters.amountPaid
      const outstanding = diff > 0 ? diff : 0
      return Math.round(outstanding * 10000) / 10000 // prevent floating point error
    },
    maxRefundable: (state, getters) => {
      // todo: use booleans to check if they are refundable
      // they will be made available with https://briqbookings.atlassian.net/browse/APP-4032
      const processorFee = getters.bookingCosts?.ProcessorFee?.IncludingTax || 0
      const merchantFee = getters.bookingCosts?.MerchantFee?.IncludingTax || 0
      const maxRefund = max(0, getters.amountPaid - processorFee - merchantFee)
      return Math.round(maxRefund * 10000) / 10000
    },
    mails: state => state.bookingDetails?.Mails,
    wasFetchedOk: state => !state.isFetching && !!state.bookingDetails && !state.fetchError,
    displayPricesIncludingTax: path(['bookingDetails', 'DisplayPricesIncludingTax']),
    scheduledBookingProducts: pipe(
      path(['bookingDetails', 'BookingProducts']),
      filter(prop('FromDateTime')),
    ),
    nonScheduledBookingProducts: pipe(
      path(['bookingDetails', 'BookingProducts']),
      filter(propEq('FromDateTime', null)), //backend should add something more explicit to indicate if the bookingproduct is scheduled or not
    ),
    numberOfVisitors: path(['bookingDetails', 'Booking', 'NumberOfPersons']),
    totalAmountPaid: pipe(
      path(['bookingDetails', 'Payments']),
      reduce((a, n) => a + n.Amount, 0),
    ),
    costsSubtotal: (state, getters) => {
      const subtotal = getters.displayPricesIncludingTax
        ? getSubtotalIncludingTax
        : getSubtotalExcludingTax
      return subtotal(getters.bookingCosts)
    },
    subtotalDiscountsAndVouchers: (state, getters) => {
      const dicsountsAndVouchers = getters.displayPricesIncludingTax
        ? totalDiscountsAndVouchersAfterTax
        : totalDiscountsAndVouchersBeforeTax
      return dicsountsAndVouchers(getters.bookingCosts)
    },
    totalCostExcludingTax: (state, getters) => getTotalExcludingTax(getters.bookingCosts),
    totalCostIncludingTax: (state, getters) =>
      getters.bookingCosts ? getTotalIncludingTax(getters.bookingCosts) : 0,
    totalTaxesByName: pipe(path(['bookingDetails', 'BookingCosts']), getTaxesByName),
    bookingIsMarkedAsInvoice,
    externalPaymentMethods: state => state.bookingDetails?.ExternalPaymentMethods,
    integrationsInfo: state => state.bookingDetails?.IntegrationsInfo,
    integrationFieldDefinitionsList: state => state.bookingDetails?.IntegrationFieldDefinitionsList,
    isSmartwaiverIntegration: (state, getters) => getters.integrationsInfo.includes('Smartwaiver'),
  },
})

// helper method for actions
const saveAction = ({ saveCall, handleResponse }) => async ({ commit }) => {
  commit('setIsSaving', true)
  commit('setSaveError', null)
  let success = true
  try {
    const response = await saveCall()
    handleResponse(response)
  } catch (e) {
    commit('setSaveError', e)
    success = false
  }
  commit('setIsSaving', false)
  return success
}

const visitorIsAdded = v => v.FirstName || v.LastName || v.Nickname

const bookingIsMarkedAsInvoice = pipe(
  path(['bookingDetails', 'Booking', 'NextBillingMethod']),
  either(equals('InvoiceNow'), equals('InvoiceAfter')),
)

export default buildModule
